import { Tabs, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { useParams, useLoaderData } from "react-router-dom";

import { AllowanceBaselineView, AllowanceBaseModel } from "@/features/allowance";
import { CostTable, CostModelForProjectBaseline } from "@/features/cost";
import { ParametricModelTable } from "@/features/parametric";
import { ReserveBaselineView, ReserveBaseModel } from "@/features/reserves";
import { RiskTable, RiskBaseModel } from "@/features/risk";
import {
  BaselineOverviewSchema,
  updateBaselineOverviewSchema,
  BaselineOverview,
} from "@/shared/components/baselineOverview";
import CustomTab from "@/shared/components/CustomTab";
import CustomTabPanel from "@/shared/components/CustomTabPanel";
import {
  BaselineTab,
  getValidTabForLevel,
} from "@/shared/context/conceptContext/ConceptContext";
import useConceptContext from "@/shared/context/conceptContext/useProjectContext";
import useProjectWBSContext from "@/shared/context/projectWBS/useProjectWBSContext";
import ParametricCostTypes from "@/shared/enums/parametricCostTypes";
import { ParameterModels } from "@/shared/types/parameterModels";

export type ProjectBaselineRouteProps = {
  parametric: ParameterModels[];
  cost: CostModelForProjectBaseline[];
  risks: RiskBaseModel[];
  allowances: AllowanceBaseModel[];
  reserves: ReserveBaseModel[];
};

export default function ProjectBaseline() {
  const theme = useTheme();
  // This is used to display error messages when the data in the table is not valid.

  // These are used to determine which data to display and which tab to highlight in the UI.
  const { outcome: wbsEntryId } = useParams();

  // This data is used to populate the tables in the UI.
  const { parametric, cost, risks, allowances, reserves } =
    useLoaderData() as ProjectBaselineRouteProps;
  // This is used to switch between different tabs in the UI. If no tab is specified in the URL, it defaults to 'parametric'.
  const { updateBaselineTab, baselineTab } = useConceptContext();

  const { getWBSEntryById, isLoading } = useProjectWBSContext();

  const WBSEntry = getWBSEntryById(wbsEntryId);
  if (!WBSEntry) return null;

  // Get a valid tab for the current level - no useEffect needed!
  const validTab = getValidTabForLevel(baselineTab, WBSEntry.level);

  // Prepare BaselineOverview data (used by all tabs)
  const parametricOutcome = parametric.find(
    //eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- we need to add a type to the parameter to compare enum with enum rather than name with enum. Must be fixed when refactoring
    (item) => item.name === ParametricCostTypes.Outcome
  )!;
  const OverviewItems = updateBaselineOverviewSchema(
    BaselineOverviewSchema,
    WBSEntry,
    parametricOutcome
  );
  const baselineOverviewTitle = WBSEntry
    ? `Level ${WBSEntry.level} — ${WBSEntry.name}`
    : "Error: Title was unable to load";

  return (
    <>
      <Box sx={{ mb: 6 }}>
        <BaselineOverview
          OverviewItems={OverviewItems}
          isLoading={isLoading}
          title={baselineOverviewTitle}
        />
      </Box>
      {WBSEntry.level === 1 || WBSEntry.level === 2 ? (
        <ParametricModelTable parameters={parametric} includeOnlyOutcome />
      ) : WBSEntry.level === 0 ? (
        <>
          <Tabs
            onChange={(_e, newValue: BaselineTab) => updateBaselineTab(newValue)}
            value={validTab}
            aria-label="tabs"
            TabIndicatorProps={{
              style: {
                background: theme.palette.primary.main,
              },
            }}>
            <CustomTab<BaselineTab>
              label="Parametric"
              value="parametric"
              to={`../project-baseline/${wbsEntryId}/parametric`}
              setActiveTab={updateBaselineTab}
            />
            <CustomTab<BaselineTab>
              label="Reserves"
              to={`../project-baseline/${wbsEntryId}/reserves`}
              value="reserves"
              setActiveTab={updateBaselineTab}
            />
          </Tabs>
          <CustomTabPanel name="parametric" value={validTab}>
            <ParametricModelTable parameters={parametric} includeOnlyOutcome />
          </CustomTabPanel>
          <CustomTabPanel name="reserves" value={validTab}>
            <ReserveBaselineView reservesData={reserves} overviewItems={OverviewItems} />
          </CustomTabPanel>
        </>
      ) : (
        <>
          <Tabs
            onChange={(_e, newValue: BaselineTab) => updateBaselineTab(newValue)}
            value={validTab}
            aria-label="tabs"
            TabIndicatorProps={{
              style: {
                background: theme.palette.primary.main,
              },
            }}>
            <CustomTab<BaselineTab>
              label="Parametric"
              value="parametric"
              to={`../project-baseline/${wbsEntryId}/parametric`}
              setActiveTab={updateBaselineTab}
            />
            <CustomTab<BaselineTab>
              label="Cost"
              to={`../project-baseline/${wbsEntryId}/cost`}
              value="cost"
              setActiveTab={updateBaselineTab}
            />
            <CustomTab<BaselineTab>
              label="Allowance"
              to={`../project-baseline/${wbsEntryId}/allowance`}
              value="allowance"
              setActiveTab={updateBaselineTab}
            />
            <CustomTab<BaselineTab>
              label="Risk"
              to={`../project-baseline/${wbsEntryId}/risk`}
              value="risk"
              setActiveTab={updateBaselineTab}
            />
          </Tabs>
          <CustomTabPanel name="parametric" value={validTab}>
            <ParametricModelTable parameters={parametric} />
          </CustomTabPanel>
          <CustomTabPanel name="cost" value={validTab}>
            <CostTable data={cost} overviewItems={OverviewItems} />
          </CustomTabPanel>
          <CustomTabPanel name="allowance" value={validTab}>
            <AllowanceBaselineView
              allowancesData={allowances}
              overviewItems={OverviewItems}
            />
          </CustomTabPanel>
          <CustomTabPanel name="risk" value={validTab}>
            <RiskTable risksData={risks} overviewItems={OverviewItems} />
          </CustomTabPanel>
        </>
      )}
    </>
  );
}
